import { randomUUID } from 'node:crypto';
import type { Server } from 'node:http';
import { createServer } from 'node:http';

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import { isInitializeRequest } from '@modelcontextprotocol/sdk/types.js';
import type { IFIXParser, Message } from 'fixparser';
import { z } from 'zod';

import { MCPBase } from './MCPBase';
import type { PluginOptions } from './PluginOptions';
import type { VerifiedOrder } from './schemas';
import type { MarketDataEntry } from './schemas/marketData';
import { toolSchemas } from './schemas/schemas';
import { createToolHandlers } from './tools';
import { handleMessage } from './utils/messageHandler';

export type RemotePluginOptions = PluginOptions & {
    port: number;
};

const transports: Record<string, StreamableHTTPServerTransport> = {};

// Helper function to convert JSON Schema to Zod schema
function jsonSchemaToZod(schema: any): z.ZodRawShape {
    if (schema.type === 'object') {
        const shape: Record<string, z.ZodTypeAny> = {};
        for (const [key, prop] of Object.entries(schema.properties || {})) {
            const propSchema = prop as any;
            if (propSchema.type === 'string') {
                if (propSchema.enum) {
                    shape[key] = z.enum(propSchema.enum as [string, ...string[]]);
                } else {
                    shape[key] = z.string();
                }
            } else if (propSchema.type === 'number') {
                shape[key] = z.number();
            } else if (propSchema.type === 'boolean') {
                shape[key] = z.boolean();
            } else if (propSchema.type === 'array') {
                if (propSchema.items.type === 'string') {
                    shape[key] = z.array(z.string());
                } else if (propSchema.items.type === 'number') {
                    shape[key] = z.array(z.number());
                } else if (propSchema.items.type === 'boolean') {
                    shape[key] = z.array(z.boolean());
                } else {
                    shape[key] = z.array(z.any());
                }
            } else {
                shape[key] = z.any();
            }
        }
        return shape;
    }
    return {};
}

export class MCPRemote extends MCPBase {
    /**
     * Port number the server will listen on.
     * @private
     */
    private port: number;

    /**
     * Node.js HTTP server instance created internally.
     * @private
     */
    private httpServer: Server | undefined;

    /**
     * MCP server instance handling MCP protocol logic.
     * @private
     */
    private mcpServer: McpServer | undefined;

    /**
     * Optional name of the plugin/server instance.
     * @private
     */
    private serverName: string | undefined;

    /**
     * Optional version string of the plugin/server.
     * @private
     */
    private serverVersion: string | undefined;

    /**
     * Map to store verified orders before execution
     * @private
     */
    protected override verifiedOrders: Map<string, VerifiedOrder> = new Map();

    /**
     * Map to store pending requests and their callbacks
     * @private
     */
    protected override pendingRequests: Map<string, (data: Message) => void> = new Map();

    /**
     * Map to store market data prices for each symbol
     * @private
     */
    protected override marketDataPrices: Map<string, MarketDataEntry[]> = new Map();

    /**
     * Maximum number of price history entries to keep per symbol
     * @private
     */
    protected override readonly MAX_PRICE_HISTORY = 100000;

    constructor({ port, logger, onReady }: RemotePluginOptions) {
        super({ logger, onReady });
        this.port = port;
    }

    public override async register(parser: IFIXParser): Promise<void> {
        this.parser = parser;
        this.logger = parser.logger;
        this.logger?.log({
            level: 'info',
            message: `FIXParser (MCP): -- Plugin registered. Creating MCP server on port ${this.port}...`,
        });

        // Add message callback handler
        this.parser.addOnMessageCallback((message: Message) => {
            if (this.parser) {
                handleMessage(
                    message,
                    this.parser,
                    this.pendingRequests,
                    this.marketDataPrices,
                    this.MAX_PRICE_HISTORY,
                );
            }
        });

        this.httpServer = createServer(async (req, res) => {
            if (!req.url || !req.method) {
                res.writeHead(400);
                res.end('Bad Request');
                return;
            }

            if (req.url === '/mcp') {
                const sessionId = req.headers['mcp-session-id'] as string | undefined;

                if (req.method === 'POST') {
                    const bodyChunks: Buffer[] = [];
                    req.on('data', (chunk) => {
                        bodyChunks.push(chunk);
                    });
                    req.on('end', async () => {
                        let parsed: Record<string, any>;
                        const body = Buffer.concat(bodyChunks).toString();
                        try {
                            parsed = JSON.parse(body);
                        } catch (error) {
                            void error;
                            res.writeHead(400);
                            res.end(JSON.stringify({ error: 'Invalid JSON' }));
                            return;
                        }

                        let transport: StreamableHTTPServerTransport;

                        if (sessionId && transports[sessionId]) {
                            transport = transports[sessionId];
                        } else if (!sessionId && req.method === 'POST' && isInitializeRequest(parsed)) {
                            transport = new StreamableHTTPServerTransport({
                                sessionIdGenerator: () => randomUUID(),
                                onsessioninitialized: (sessionId) => {
                                    transports[sessionId] = transport;
                                },
                            });

                            transport.onclose = () => {
                                if (transport.sessionId) {
                                    delete transports[transport.sessionId];
                                }
                            };

                            this.mcpServer = new McpServer({
                                name: this.serverName || 'FIXParser',
                                version: this.serverVersion || '1.0.0',
                            });

                            this.setupTools();

                            await this.mcpServer.connect(transport);
                        } else {
                            res.writeHead(400, { 'Content-Type': 'application/json' });
                            res.end(
                                JSON.stringify({
                                    jsonrpc: '2.0',
                                    error: {
                                        code: -32000,
                                        message: 'Bad Request: No valid session ID provided',
                                    },
                                    id: null,
                                }),
                            );
                            return;
                        }

                        try {
                            await transport.handleRequest(req, res, parsed);
                        } catch (error) {
                            this.logger?.log({
                                level: 'error',
                                message: `Error handling request: ${error}`,
                            });
                            throw error;
                        }
                    });
                } else if (req.method === 'GET' || req.method === 'DELETE') {
                    if (!sessionId || !transports[sessionId]) {
                        res.writeHead(400);
                        res.end('Invalid or missing session ID');
                        return;
                    }
                    const transport = transports[sessionId];
                    try {
                        await transport.handleRequest(req, res);
                    } catch (error) {
                        this.logger?.log({
                            level: 'error',
                            message: `Error handling ${req.method} request: ${error}`,
                        });
                        throw error;
                    }
                } else {
                    this.logger?.log({
                        level: 'error',
                        message: `Method not allowed: ${req.method}`,
                    });
                    res.writeHead(405);
                    res.end('Method Not Allowed');
                }
            } else {
                res.writeHead(404);
                res.end('Not Found');
            }
        });

        this.httpServer.listen(this.port, () => {
            this.logger?.log({
                level: 'info',
                message: `FIXParser (MCP): -- Server listening on http://localhost:${this.port}...`,
            });
        });

        if (this.onReady) {
            this.onReady();
        }
    }

    private setupTools(): void {
        if (!this.parser) {
            this.logger?.log({
                level: 'error',
                message: 'FIXParser (MCP): -- FIXParser instance not initialized. Ignoring setup of tools...',
            });
            return;
        }

        if (!this.mcpServer) {
            this.logger?.log({
                level: 'error',
                message: 'FIXParser (MCP): -- MCP Server not initialized. Ignoring setup of tools...',
            });
            return;
        }

        // Create tool handlers
        const toolHandlers = createToolHandlers(
            this.parser,
            this.verifiedOrders,
            this.pendingRequests,
            this.marketDataPrices,
        );

        // Register each tool with its schema
        Object.entries(toolSchemas).forEach(([name, { description, schema }]) => {
            this.mcpServer?.registerTool(
                name,
                {
                    description,
                    inputSchema: jsonSchemaToZod(schema),
                },
                async (args) => {
                    const handler = toolHandlers[name];
                    if (!handler) {
                        return {
                            content: [
                                {
                                    type: 'text',
                                    text: `Tool not found: ${name}`,
                                },
                            ],
                            isError: true,
                        };
                    }

                    const result = await handler(args);
                    return {
                        content: result.content,
                        isError: result.isError,
                    };
                },
            );
        });
    }
}
